import { apiRequestForWrapper, wrapperAPICall } from "../api/apiRequest";
import { getRequestLogger } from "../api/apiTester";
import { isTestEnvironment } from '../apiCaching/POST-Requests/helperModules/helperFunctions';

const testEnvironment: boolean = isTestEnvironment();
const CACHE_NAME = "opus2.0-api-cache";
const displayCacheLog = (process.env.SHOW_CACHE_LOG && typeof JSON.parse(process.env.SHOW_CACHE_LOG.toLowerCase()) === 'boolean') ? JSON.parse(process.env.SHOW_CACHE_LOG.toLowerCase()) : false;

//fetching data from API
async function fetchDataFromAPI(url: string) {
  try {
    // During purge, bypass service worker and go directly to network
    if (window.__cachePurgeInProgress) {
      const response = await fetch(url, {
        cache: 'no-store', // Bypass cache completely
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    }

    const response = await apiRequestForWrapper({ method: "GET", url });
    return response;
  } catch (error) {
    const errorResponse = await handleApiError(error, url);
    if (errorResponse) {
      throw errorResponse;
    }
    console.error("Error fetching data from API:", error);
    throw error;
  }
}

//fetching data from API
async function fetchWrapper(url: string) {
  try {
    const response = await wrapperAPICall({ method: "GET", url });
    if (testEnvironment) {
      console.log(url);
    }
    return response;
  } catch (error) {

    console.error("Error fetching data from API:", error);
    const errorResponse = await handleApiError(error, url);
    if (errorResponse) {
      throw errorResponse;
    }
    throw error;
  }
}

async function handleApiError(error: any, callUrl?: any) {
  let errObj = error;
  let errMsg = '';

  // If error is a Promise, await it
  if (error instanceof Promise) {
    try {
      errObj = await error;
    } catch (e) {
      errObj = e;
    }
  }

  // If error is a fetch Response object, try to extract JSON message
  if (errObj instanceof Response) {
    try {
      const data = await errObj.json();
      errMsg = data?.message ?? JSON.stringify(data);
      errObj = data;
    } catch (e) {
      console.error("Error parsing response JSON:", e);
      errMsg = errObj.statusText ?? `HTTP error! status: ${errObj.status}`;
    }
  } else if (errObj instanceof Error) {
    errMsg = errObj.message;
    if (errMsg === 'Full address is needed to narrow down results') {
      return new Error('Full address is needed to narrow down results');
    }
  } else if (typeof errObj === 'object' && errObj !== null && 'message' in errObj) {
    errMsg = errObj.message;
  } else {
    errMsg = String(errObj);
  }
  if (testEnvironment) {
    console.log(callUrl);
  }
  tryLogging(callUrl, errMsg);
  console.log(errObj);
  return errObj;
}
//Function to check to trim whitespaces from the zipcode, address and check if api key is missing
function validateUrl(url: string, apiKeyNeeded: boolean) {
  try {
    const urlObj = new URL(url);
    const urlParams = urlObj.searchParams;
    const postalCode = urlParams.get('PostalCode');
    const address = urlParams.get('Address');
    // Trim whitespaces from the PostalCode parameter
    if (postalCode) {
      urlParams.set('PostalCode', postalCode.trim());
    }
    // Trim whitespaces from the Address parameter
    if (address) {
      urlParams.set('Address', address.trim());
    }

    if (apiKeyNeeded) {
      // Check if the apikey parameter is present
      let apiKey = null;
      urlParams.forEach((value, key) => {
        if (key.toLowerCase() === 'apikey') {
          apiKey = value;
        }
      });

      if (!apiKey) {
        console.error("API key is missing in the URL");
        return null;
      }
    }


    return urlObj.toString();
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

//Function to fetch the cached data
async function getCachedData(url: string, cacheExpiry: number) {
  const cache = await caches.open(CACHE_NAME);
  const response = await cache.match(url);
  if (!response) {
    return null;
  }
  const cachedData = await response.json();
  const isCacheValid = Date.now() - cachedData.timestamp < cacheExpiry * 60 * 1000;

  if (isCacheValid) {
    if (displayCacheLog)
      console.info(`C_Hit: ${url}`);
    return cachedData.data;
  }

  // Cache is expired - delete it gracefully
  try {
    await cache.delete(url);
  } catch (error) {
    console.warn('Failed to delete expired cache:', error);
  }
  return null;
}

//Function to set the cached data
async function setCacheData(url: string, freshData: any) {
  const cache = await caches.open(CACHE_NAME);
  const cacheData = { timestamp: Date.now(), data: freshData };
  cache.put(url, new Response(JSON.stringify(cacheData)));
  if (displayCacheLog)
    console.log("C_Set ", url)
}

// Helper functions for getCachedAPIResponse
async function handleCacheResponse(validatedUrl: string, duration: number) {
  const cachedAPIResponse = await getCachedData(validatedUrl, duration);
  if (cachedAPIResponse) {
    return cachedAPIResponse;
  }
  return null;
}

async function handleFreshData(validatedUrl: string) {
  const freshData = await fetchDataFromAPI(validatedUrl);

  if (!window.__cachePurgeInProgress && checkContent(freshData)) {
    try {
      await setCacheData(validatedUrl, freshData);
    } catch (error) {
      console.warn('Failed to cache data:', error);
    }
  }
  else if (!checkContent(freshData)) {
    console.error("Empty API response");
    tryLogging(validatedUrl, "Empty API response");
  }
  return freshData;
}

async function handleCacheOperation(validatedUrl: string, duration: number, url: string) {
  try {
    const cachedResponse = await handleCacheResponse(validatedUrl, duration);
    if (cachedResponse) return cachedResponse;

    return await handleFreshData(validatedUrl);
  } catch (error) {
    console.error("Error with Cache API:", error);
    // On error, try direct API call as fallback
    return await fetchDataFromAPI(validatedUrl);
  }
}

//Final function exported to all pages for all get requests.
export async function getCachedAPIResponse(url: string, duration = 10, apiKeyNeeded = false, cacheResult = true) {
  const validatedUrl = validateUrl(url, apiKeyNeeded);
  if (!validatedUrl) return null;

  // During purge, always bypass cache
  if (window.__cachePurgeInProgress) {
    return fetchDataFromAPI(validatedUrl);
  }

  // If caching is not requested or not supported, fetch directly
  if (!cacheResult || !("caches" in window)) {
    if (!cacheResult) {
      return fetchDataFromAPI(validatedUrl);
    }
    console.warn("Caching not present in this browser");
    return fetchDataFromAPI(validatedUrl);
  }

  return handleCacheOperation(validatedUrl, duration, url);
}

export async function cachedAPIWrapper(url: string, duration = 10, cacheResult = true) {
  const validatedUrl = validateUrl(url, false);
  if (!validatedUrl) {
    return null;
  }

  // During purge, always bypass cache
  if (window.__cachePurgeInProgress) {
    return fetchWrapper(validatedUrl);
  }

  if (cacheResult) {
    // Check if caching is supported in the browser
    if (!("caches" in window)) {
      console.warn("Caching not present in this browser");
      return fetchWrapper(validatedUrl);
    }

    try {
      const cachedAPIResponse = await getCachedData(validatedUrl, duration);
      if (cachedAPIResponse) {
        return cachedAPIResponse;
      }

      const freshData = await fetchWrapper(validatedUrl);
      if (!window.__cachePurgeInProgress && checkContent(freshData)) {
        await setCacheData(validatedUrl, freshData);
      }
      else if (!checkContent(freshData)) {
        console.error("Empty API response");
        tryLogging(validatedUrl, "Empty API response");
      }
      return freshData;
    } catch (error) {
      console.error("Error with Cache API:", error);
      return fetchWrapper(validatedUrl); // Direct API call as fallback
    }
  } else {
    return fetchWrapper(validatedUrl);
  }
}

function checkContent(freshData: any): any {
  if (!Array.isArray(freshData)) {
    return freshData;
  }
  if (freshData.length > 0) {
    return freshData;
  }
  return null;
}

function tryLogging(validatedUrl?: string, errMsg?: string) {
  try {
    getRequestLogger(validatedUrl, errMsg);
  }
  catch (err) {
    console.error("Request logging to datadog failed...", err);
    return;
  }

}